import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageSquare, Clock, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const Support = () => {
  useDocumentMeta({
    title: "Support — Get Help with Your Antivirus License",
    description:
      "Need help with your Huntheal Antivirus license? Call +1 (888) 655-4464 or email support@prosoftmarketplace.com — we respond within an hour.",
  });

  const topics = [
    {
      title: "Where's my activation key?",
      body:
        "Your activation key is on the confirmation page right after PayPal checkout, and a copy is emailed to the address you provided. Check your spam folder if you don't see it within 5 minutes.",
    },
    {
      title: "How do I install my antivirus?",
      body:
        "After purchase, visit huntheal.com, download the installer for Windows, run it, and paste the activation key from your order email. Detailed step-by-step instructions are also included in your order email.",
    },
    {
      title: "I want to switch antivirus brands",
      body:
        "Uninstall your current antivirus first (use the brand's official removal tool to ensure a clean uninstall), then install your new license. No overlap needed.",
    },
    {
      title: "My subscription is expiring — what now?",
      body:
        "Your antivirus vendor will renew automatically at their standard price. To save again, turn auto-renew OFF in your vendor account and buy a fresh year from us at our bulk price.",
    },
    {
      title: "I bought the wrong product",
      body:
        "No problem. You have 30 days to request a refund. Call +1 (888) 655-4464 or email support@prosoftmarketplace.com with your order reference (starts with NRT-) and we'll process it within 1 business day.",
    },
  ];

  return (
    <div data-testid="support-page" className="bg-slate-50">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Help center</div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">
            We&apos;re here to help.
          </h1>
          <p className="mt-3 max-w-2xl text-base text-slate-600">
            Most questions get answered in 5 minutes by phone, or within an hour by email.
            Browse the common topics below or contact us directly.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-3">
          <ContactCard
            icon={Phone}
            label="Call us"
            value="+1 (888) 655-4464"
            href="tel:+18886554464"
            sub="Toll-free, US-based agents"
            testid="support-call-card"
          />
          <ContactCard
            icon={Mail}
            label="Email"
            value="support@prosoftmarketplace.com"
            href="mailto:support@prosoftmarketplace.com"
            sub="Reply within 1 hour, biz hours"
            testid="support-email-card"
          />
          <ContactCard
            icon={MapPin}
            label="Visit"
            value="2118 Glenridge Ct, Marietta, GA"
            href="https://maps.google.com/?q=2118+Glenridge+Ct+Marietta+GA+30062"
            sub="Appointments only"
            testid="support-visit-card"
          />
        </div>

        <div className="mt-10 border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            Common topics
          </div>
          <ul className="divide-y divide-slate-200">
            {topics.map((t) => (
              <li key={t.title} className="px-6 py-5">
                <div className="font-display text-lg font-bold tracking-tight text-slate-900">
                  {t.title}
                </div>
                <p className="mt-1 text-sm leading-relaxed text-slate-700">{t.body}</p>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-10 border-2 border-slate-900 bg-white p-6 shadow-[8px_8px_0_0_rgba(16,185,129,1)] sm:flex sm:items-center sm:justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">
              Still stuck?
            </div>
            <h2 className="mt-1 font-display text-2xl font-black tracking-tighter text-slate-900">
              Talk to a real human.
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              <Clock className="inline h-3 w-3" /> Mon–Sat, 8am–8pm ET
            </p>
          </div>
          <div className="mt-4 flex flex-col gap-2 sm:mt-0 sm:flex-row">
            <Button asChild data-testid="support-call-btn" className="rounded-sm bg-slate-900 text-white hover:bg-slate-800">
              <a href="tel:+18886554464">
                <Phone className="mr-1 h-4 w-4" /> +1 (888) 655-4464
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="support-contact-btn"
              className="rounded-sm border-slate-300 hover:border-slate-900"
            >
              <Link to="/contact">
                <MessageSquare className="mr-1 h-4 w-4" /> Contact form <ChevronRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

const ContactCard = ({ icon: Icon, label, value, href, sub, testid }) => (
  <a
    href={href}
    data-testid={testid}
    className="group block border border-slate-200 bg-white p-6 transition-all hover:border-slate-900 hover:shadow-[6px_6px_0_0_rgba(15,23,42,1)]"
  >
    <div className="flex h-10 w-10 items-center justify-center bg-emerald-500">
      <Icon className="h-5 w-5 text-white" />
    </div>
    <div className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
    <div className="mt-1 font-display text-lg font-bold tracking-tight text-slate-900 group-hover:text-emerald-600">
      {value}
    </div>
    <div className="mt-1 text-xs text-slate-500">{sub}</div>
  </a>
);

export default Support;
